// /oauth/authorize: Claude, ChatGPT or another assistant asks to connect to this person's Chefi (OAuth 2.1, see
// cloudflare/src/oauth.ts). The person signs in with their passkey, sees who is asking and where it sends them back,
// chooses read only or read and suggest, and allows or denies. The browser then returns to the assistant.
import React, { useEffect, useState } from 'react';
import { useRoute, type RouteProp } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { Body, Button, Caption, Choice, Row, Screen, Section, Title } from '../components/ui';
import ErrorNote from '../components/ErrorNote';
import * as ai from '../platform/ai';
import * as auth from '../platform/auth';
import { ApiError } from '../platform/auth';
import { useAccount, withReauth } from '../store/account';
import { useNav, type AllParams } from '../navigation';
import { usePalette } from '../theme';

type Client = Awaited<ReturnType<typeof ai.oauthClient>>;

export default function OAuthScreen() {
  const { t } = useTranslation();
  const p = usePalette();
  const nav = useNav();
  const params = (useRoute<RouteProp<AllParams, 'OAuthAuthorize'>>().params ?? {}) as Record<string, string>;
  const { user, status, busy, run } = useAccount();
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [grant, setGrant] = useState<'read' | 'propose' | 'full'>('propose');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) return;
    ai.oauthClient(params).then(
      c => {
        setClient(c);
        if (!c.scopes.includes('propose')) setGrant(c.scopes.includes('full') ? 'full' : 'read');
      },
      e => setError(e instanceof ApiError ? e.code : 'generic'),
    );
  }, [user, params.client_id, params.redirect_uri]); // eslint-disable-line react-hooks/exhaustive-deps

  const answer = async (allow: boolean) => {
    setSending(true);
    setError(null);
    try {
      const { redirect } = await withReauth(() => ai.oauthAnswer(params, allow, grant));
      ai.leaveTo(redirect);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
      setSending(false);
    }
  };

  // The name a client registers is its own claim; only the return address proves Claude or ChatGPT.
  const name = client?.known ?? (client ? t('oauth.unverified', { name: client.name }) : '');
  return (
    <Screen>
      <Title>{t('oauth.title')}</Title>
      {!ai.aiAvailable ? <Body secondary>{t('ai.serverNative')}</Body> : status === 'loading' ? null : !user ? (
        <Section>
          <Body>{t('oauth.signInFirst')}</Body>
          <Row>
            <Button label={t('account.signIn')} icon="user" disabled={busy} onPress={() => run(() => auth.signIn())} />
            <Button label={t('oauth.createAccount')} variant="secondary" onPress={() => nav.navigate('Account')} />
          </Row>
          <ErrorNote />
        </Section>
      ) : client ? (
        <Section>
          <Body>{t('oauth.asks', { name })}</Body>
          <Caption>{t('oauth.returnsTo', { host: client.host })}{client.known ? '' : ` ${t('oauth.unknown')}`}</Caption>
          {client.scopes.length > 1 ? (
            <Choice options={(['propose', 'full'] as const).filter(value => client.scopes.includes(value))} value={grant} onChange={setGrant} labels={v => t(`ai.scopes.${v}`)} />
          ) : null}
          <Caption>{t(`ai.scopeNotes.${grant}`)}</Caption>
          <Caption>{t('oauth.never')}</Caption>
          <Row>
            <Button label={t('oauth.allow', { name })} icon="check" loading={sending} disabled={sending} onPress={() => answer(true)} />
            <Button label={t('oauth.deny')} variant="secondary" disabled={sending} onPress={() => answer(false)} />
          </Row>
          <Caption>{t('oauth.manage')}</Caption>
        </Section>
      ) : null}
      {error ? <Caption color={p.danger}>{t(`oauth.errors.${error}`, { defaultValue: t('ai.errors.generic') })}</Caption> : null}
    </Screen>
  );
}
